import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityOptions,
  ActivityOrderBy,
  DateTypeFilter,
  getActivityFilter,
  Severity,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { array, boolean, string, object as yupObject } from 'yup';
import { FilterScreenLayout } from './filterScreenLayout';
import { ACTIVITY_SECTIONS } from './filterSectionTypes';

const formSchema = yupObject({
  orderBy: string().optional().nullable(),
  severity: array().of(string()).optional().nullable(),
  groupIds: array().of(string()).optional().nullable(),
  labelIds: array().of(string()).optional().nullable(),
  isCombinedGroupsFilter: boolean().optional().nullable(),
  activityOption: string().optional().nullable(),
  dateType: string().optional().nullable(),
  startDate: string().optional().nullable(),
  endDate: string().optional().nullable(),
});

export type ActivityFilterScreenProps = {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  filterKey?: ActivityFilterKey;
};

export function ActivityFilterScreen({
  mode,
  onBack,
  onDismiss,
  isVisible,
  filterKey = ActivityFilterKey.Activity,
}: ActivityFilterScreenProps) {
  const { data: filter } = useActivityListFilter(filterKey);
  const { mutateAsync: setActivityListFilter } = useSetActivityListFilter(filterKey);
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();

  const getInitialSeverity = () => {
    if (!filter?.severity || filter.severity.length === 0) return [];
    const allSeverities = Object.values(Severity);
    const hasAllSeverities =
      filter.severity.length === allSeverities.length &&
      allSeverities.every((s) => filter.severity?.includes(s));
    return hasAllSeverities ? [] : filter.severity;
  };

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      orderBy: filter?.orderBy ?? ActivityOrderBy.MostRecentUpdated,
      severity: getInitialSeverity(),
      groupIds: filter?.groupIds ?? [],
      labelIds: filter?.labelIds ?? [],
      isCombinedGroupsFilter: filter?.isCombinedGroupsFilter ?? false,
      activityOption: filter?.activityOption ?? ActivityOptions.EVERYTHING,
      dateType: filter?.dateType ?? DateTypeFilter.ALL,
      startDate: filter?.startDate ?? '',
      endDate: filter?.endDate ?? '',
    },
  });

  const { control, reset, watch, setValue } = formMethods;
  const formData = watch();
  const selectedDateType = watch('dateType');

  const isGlobalAdmin = account?.isGlobalAdmin?.() || false;
  const workspaceId =
    currentWorkspace?.id ?? (isGlobalAdmin ? undefined : account?.workspace?.id);

  const buildServerFilter = () => {
    const payload: any = { ...formData };
    if (!payload.severity || payload.severity.length === 0) delete payload.severity;
    if (payload.activityOption === ActivityOptions.EVERYTHING) delete payload.activityOption;
    if (!payload.groupIds || payload.groupIds.length === 0) delete payload.groupIds;
    if (!payload.labelIds || payload.labelIds.length === 0) delete payload.labelIds;
    const hasActiveFilters =
      payload.severity ||
      payload.activityOption ||
      (payload.groupIds?.length > 0) ||
      (payload.labelIds?.length > 0) ||
      payload.dateType !== DateTypeFilter.ALL;
    if (!hasActiveFilters && !workspaceId) return undefined;
    const activityFilterModel = new ActivityFilterModel({ ...filter, ...payload });
    return workspaceId ? getActivityFilter(activityFilterModel, workspaceId) : activityFilterModel.getFilter();
  };

  const serverFilterForCount = buildServerFilter();
  const { data: countData } = useCustomQuery({
    cacheKey: ['activities', 'filterCount', isGlobalAdmin ? 'global' : workspaceId || 'none', JSON.stringify(serverFilterForCount)],
    query: `
      query FindIssuesCount($filter: FindIssuesFilter, $page: PageRequest) {
        issue {
          findIssues(filter: $filter, page: $page) {
            count
          }
        }
      }
    `,
    variables: { filter: serverFilterForCount, page: { limit: 1, offset: 0 } },
    options: { enabled: true },
  });
  const resultsCount = countData?.issue?.findIssues?.count ?? 0;

  const onCloseAfterApply = mode === 'page' ? onBack : onDismiss;

  const onSubmit = async (data: any) => {
    const payload = { ...data };
    if (!payload.severity || payload.severity.length === 0) payload.severity = undefined;
    await setActivityListFilter(new ActivityFilterModel({ ...filter, ...payload }));
    onCloseAfterApply?.();
  };

  const onReset = async () => {
    const defaultData = {
      orderBy: ActivityOrderBy.MostRecentUpdated,
      severity: [],
      activityOption: ActivityOptions.EVERYTHING,
      groupIds: [],
      labelIds: [],
      isCombinedGroupsFilter: false,
      dateType: DateTypeFilter.ALL,
      startDate: '',
      endDate: '',
    };
    reset(defaultData);
    await setActivityListFilter(new ActivityFilterModel({ ...filter, ...defaultData, severity: undefined }));
  };

  const canReset = () =>
    (formData.severity && formData.severity.length > 0) ||
    formData.activityOption !== ActivityOptions.EVERYTHING ||
    (formData.groupIds && formData.groupIds.length > 0) ||
    (formData.labelIds && formData.labelIds.length > 0) ||
    formData.dateType !== DateTypeFilter.ALL ||
    formData.orderBy !== ActivityOrderBy.MostRecentUpdated;

  return (
    <FilterScreenLayout
      mode={mode}
      onBack={onBack}
      onDismiss={onDismiss}
      isVisible={isVisible}
      titleKey="activity.filter.title"
      formMethods={formMethods}
      resultsCount={resultsCount}
      viewResultsTranslationKey="activity.filter.viewResults"
      onReset={onReset}
      canReset={canReset}
      onSubmit={onSubmit}
      variant="activity"
      sections={ACTIVITY_SECTIONS}
      control={control}
      setValue={setValue}
      selectedDateType={selectedDateType ?? ''}
      workspaceId={workspaceId}
      isGlobalAdmin={isGlobalAdmin}
    />
  );
}
